import { z } from 'zod';
import { CurrencySchema } from './stores.js';
import { OfferCategoryCountSchema, OfferCategorySchema } from './categories.js';
import { toCzkMinor } from './money.js';
/**
 * Akce jen z jedne verze letaku: pro obchody s pultem s obsluhou, nebo bez nej
 * (Biedronka); z letaku pro hypermarkety, nebo supermarkety (Tesco, Albert).
 */
export const OfferVariantSchema = z.enum(['counter', 'no_counter', 'hypermarket', 'supermarket']);
/** Vyrez akce ze stranky letaku: souradnice 0–1 od leveho horniho rohu, ratio = sirka/vyska stranky. */
export const OfferCropSchema = z.object({
    x0: z.number().min(0).max(1),
    y0: z.number().min(0).max(1),
    x1: z.number().min(0).max(1),
    y1: z.number().min(0).max(1),
    ratio: z.number().positive().max(10),
});
/**
 * Akce z letaku, jak je vraci API. Nazvy zustavaji v jazyce obchodu
 * (polsky / nemecky) — sestra v regalu hleda „Masło Ekstra", ne „Máslo".
 * Cesky preklad je vedle v nameCs. Nove pole maji default kvuli datum
 * ulozenym v telefonu starsi verzi.
 */
export const OfferSchema = z.object({
    id: z.uuid(),
    chain: z.string(),
    name: z.string(),
    brand: z.string().nullable(),
    description: z.string().nullable(),
    priceMinor: z.number().int().nonnegative().nullable(),
    oldPriceMinor: z.number().int().nonnegative().nullable(),
    currency: CurrencySchema,
    /** „8,60 €/kg" — jak ho uvadi letak. */
    unitPrice: z.string().nullable(),
    requiresCard: z.boolean(),
    validFrom: z.iso.datetime().nullable(),
    validTo: z.iso.datetime().nullable(),
    leafletName: z.string().nullable(),
    url: z.string().nullable(),
    /** Obrazek produktu, u Blixu cela stranka letaku a vyrez v imageCrop. */
    imageUrl: z.string().nullable(),
    imageCrop: OfferCropSchema.nullable().default(null),
    /** Cesky strojovy preklad nazvu; null = zatim neprelozeno nebo preklad vypnuty. */
    nameCs: z.string().nullable().default(null),
    /** null = akce je ve vsech verzich letaku. */
    variant: OfferVariantSchema.nullable().default(null),
    /** null = zatim nezarazeno (na strance Akce patri do „Ostatní"). */
    category: OfferCategorySchema.nullable().default(null),
});
export const OffersResponseSchema = z.object({
    offers: z.array(OfferSchema),
    total: z.number().int().nonnegative(),
    /** Kdy se naposledy povedlo nacist nektery ze zdroju skupiny. */
    updatedAt: z.iso.datetime().nullable(),
    /** Retezce obchodu skupiny, pro ktere se akce nacitaji. */
    chains: z.array(z.string()),
    /** S dnem nakupu: do kdy jsou zname tydenni letaky; null = bez dne nebo zadne akce. */
    knownUntil: z.iso.datetime().nullable().default(null),
    /** S hledanim: kolik prvnich akci ma vsechna hledana slova, dalsi jsou podobne; null = bez hledani. */
    exact: z.number().int().nonnegative().nullable().default(null),
    /** Pocty akci po kategoriich pro stejne filtry bez kategorie; jen neprazdne, v poradi OFFER_CATEGORIES. */
    categories: z.array(OfferCategoryCountSchema).default([]),
});
/**
 * Rucni obnova letaku ze stranky Akce (rodice jezdi ve ctvrtek, letaky vychazi
 * ve stredu vecer): started = sber bezi na pozadi, running = uz bezi jiny,
 * recent = skupina obnovovala pred chvili (nextAt = kdy znovu).
 */
export const OffersRefreshResponseSchema = z.object({
    state: z.enum(['started', 'running', 'recent']),
    nextAt: z.iso.datetime().nullable(),
});
/** Stav sberu pro stranku Akce: bezi a kdy se naposledy povedlo nacist zdroj skupiny. */
export const OffersRefreshStatusSchema = z.object({
    running: z.boolean(),
    updatedAt: z.iso.datetime().nullable(),
});
export const OfferMatchSchema = z.object({
    catalogItemId: z.uuid(),
    offers: z.array(OfferSchema),
    /** Sablony, ve kterych zbozi je. Default kvuli akcim ulozenym v telefonu starsi verzi. */
    templateIds: z.array(z.uuid()).default([]),
});
export const OfferMatchesResponseSchema = z.object({ matches: z.array(OfferMatchSchema) });
/** Prodejna Kauflandu v Cesku — akce se mezi prodejnami lisi, u obchodu se vybira. */
export const KauflandStoreSchema = z.object({
    id: z.string(),
    name: z.string(),
    city: z.string().nullable(),
    zip: z.string().nullable(),
});
export const KauflandStoresResponseSchema = z.object({ stores: z.array(KauflandStoreSchema) });
/** Kolik akci z jednoho retezce se u polozky sablony nabidne. */
export const MAX_OFFERS_PER_CHAIN = 3;
/**
 * Akce jedne polozky od nejlevnejsi; zloty a eura se porovnaji v korunach.
 * Akce bez ceny nebo bez kurzu jdou na konec, jinak zustava puvodni poradi.
 */
export function rankOffers(offers, fx) {
    return offers
        .map((offer, index) => ({
        offer,
        index,
        czk: offer.priceMinor === null ? null : toCzkMinor(offer.priceMinor, offer.currency, fx),
    }))
        .sort((a, b) => Number(a.czk === null) - Number(b.czk === null) || (a.czk ?? 0) - (b.czk ?? 0) || a.index - b.index)
        .map((ranked) => ranked.offer);
}
export function itemOfferSummary(offers, 
/** Obchod polozky; null = polozka bez obchodu. */
store, fx) {
    if (!offers || offers.length === 0)
        return null;
    const ranked = rankOffers(offers, fx);
    const chains = [...new Set(ranked.map((offer) => offer.chain))];
    if (!store)
        return { kind: 'any', offer: ranked[0], count: ranked.length, chains };
    const here = ranked.filter((offer) => offer.chain === store.offerChain);
    if (here.length > 0)
        return { kind: 'here', offer: here[0], count: here.length };
    return { kind: 'elsewhere', chains };
}
/**
 * Akce polozky po retezcich pro detail polozky: retezec obchodu polozky prvni
 * (i prazdny — „tady teď v akci není"), ostatni podle nejlevnejsi akce v korunach,
 * uvnitr retezce od nejlevnejsi. ownChain null = polozka bez obchodu nebo obchod bez letaku.
 */
export function offersByChain(offers, ownChain, fx) {
    const groups = new Map();
    if (ownChain)
        groups.set(ownChain, { chain: ownChain, own: true, offers: [] });
    for (const offer of rankOffers(offers, fx)) {
        const group = groups.get(offer.chain) ?? { chain: offer.chain, own: false, offers: [] };
        group.offers.push(offer);
        groups.set(offer.chain, group);
    }
    return [...groups.values()];
}
/**
 * Text pro hledani: bez diakritiky, malymi pismeny, jen pismena a cislice.
 * „Masło Ekstra 82%" -> „maslo ekstra 82%". Polske ł a nemecke ß se v Unicode
 * na zaklad nerozkladaji, proto zvlast.
 */
export function offerSearchText(text) {
    return text
        .normalize('NFKD')
        .replace(/\p{M}/gu, '')
        .replace(/[łŁ]/g, 'l')
        .replace(/ß/g, 'ss')
        .toLowerCase()
        .replace(/[^a-z0-9%]+/g, ' ')
        .trim();
}
/** Koncovky ceskych pridavnych jmen a padu, nejdelsi prvni. */
const CZECH_SUFFIXES = ['ovych', 'ovymi', 'ovemu', 'oveho', 'ovou', 'ovy', 'ova', 'ove', 'ovi', 'ych', 'ymi', 'emu', 'eho', 'ich', 'imi', 'ami', 'ach', 'ech', 'ou', 'em'];
/**
 * Kmen slova pro hledani: u delsich slov bez koncovky, at „plátkový" najde
 * „plátky" a „čokolády" „čokoláda" (a pres cesky preklad i polskou „czekolada").
 * Kmen ma aspon 3 pismena, kratka slova a cisla zustavaji.
 */
export function searchStem(word) {
    if (word.length < 5 || !/^[a-z]+$/.test(word))
        return word;
    for (const suffix of CZECH_SUFFIXES) {
        if (word.endsWith(suffix) && word.length - suffix.length >= 3)
            return word.slice(0, -suffix.length);
    }
    const stripped = word.replace(/[aeiouy]+$/, '');
    return stripped.length >= 3 ? stripped : word;
}
/**
 * Kmeny, ktere v letacich znamenaji totez. „Plátkový sýr" je v Biedronce
 * „w plastrach" a v preklad „nakrájený" (Swiatowid, 2026-10-13) — bez nich
 * clovek musel znat presny nazev z letaku.
 */
const SEARCH_SYNONYMS = [
    ['platk', 'platek', 'nakrajen', 'krajen', 'plastr', 'plaster', 'scheib'],
    ['strouhan', 'starty', 'reibe', 'gerieben'],
];
/** Kmen slova a jeho souznacne kmeny — staci shoda s kterymkoli. */
export function searchAlternatives(token) {
    const stem = searchStem(token);
    const group = stem.length >= 4 ? SEARCH_SYNONYMS.find((g) => g.some((s) => stem.startsWith(s) || s.startsWith(stem))) : undefined;
    return [...new Set([stem, ...(group ?? [])])];
}
/** Slova hledaneho textu pro LIKE nad search_text, jako kmeny (searchStem). */
export function offerQueryTokens(query) {
    return offerSearchText(query)
        .replace(/%/g, ' ')
        .split(' ')
        .filter(Boolean)
        .map(searchStem)
        .slice(0, 6);
}
/**
 * Kolik slov hledani musi akce mit: jedno nebo dve slova staci jedno (zbytek
 * ukaze razeni), u delsiho hledani muze chybet jedno. Plne shody jdou prvni.
 */
export function offerQueryMinScore(tokens) {
    return tokens <= 2 ? Math.min(1, tokens) : tokens - 1;
}
/**
 * Hledane vyrazy polozky sablony jako seznam slov. Bez vlastnich vyrazu se
 * zkusi cesky nazev — search_text akce obsahuje i cesky preklad nazvu, bez
 * prekladu pomuze aspon blizkost polstiny („mleko", „maslo", „chleb").
 */
export function offerTermTokens(item) {
    return offerTermSources(item)
        .map((term) => offerSearchText(term)
        .split(' ')
        .filter((word) => word.length >= 2 || /\d/.test(word)))
        .filter((tokens) => tokens.length > 0);
}
/** Jazyky, do kterych se prekladaji hledane vyrazy polozek — podle zemi obchodu skupiny. */
export const TERM_LANGS = ['de', 'pl'];
/** Vyrazy, ktere polozka posila prekladaci: vlastni, jinak nazev. */
export function offerTermSources(item) {
    const own = (item.offerTerms ?? '')
        .split(/[,;\n]/)
        .map((t) => t.trim())
        .filter(Boolean);
    return own.length > 0 ? own : [item.name.trim()].filter(Boolean);
}
/** Clen nebo spojka z prekladu by v nazvu akce chybela a shodu by zkazila. */
const TRANSLATION_STOPWORDS = new Set(['der', 'die', 'das', 'ein', 'eine', 'und', 'mit', 'na', 'ze', 'do']);
export function itemOfferTerms(item, translated = []) {
    const tokens = (text) => offerSearchText(text)
        .split(' ')
        .filter((word) => (word.length >= 2 || /\d/.test(word)) && !TRANSLATION_STOPWORDS.has(word));
    const prefix = offerTermTokens(item);
    const whole = [];
    const seen = new Set(prefix.map((t) => t.join(' ')));
    for (const { lang, text } of translated) {
        const words = tokens(text);
        const key = words.join(' ');
        if (words.length === 0 || seen.has(key))
            continue;
        seen.add(key);
        (lang === 'de' ? whole : prefix).push(words);
    }
    return { prefix, whole };
}
/** Cele slovo, nanejvys se dvema pismeny koncovky („Nudel" → „Nudeln"). */
function wholeWord(word, token) {
    return word.startsWith(token) && (word.length - token.length <= 2 || /\d/.test(token));
}
export function offerMatchesItem(terms, searchText) {
    if (!searchText)
        return false;
    if (offerMatchesTerms(terms.prefix, searchText))
        return true;
    const words = searchText.split(' ');
    return terms.whole.some((tokens) => tokens.every((token) => words.some((word) => wholeWord(word, token))));
}
/**
 * Shoda: nektery z vyrazu ma vsechna slova jako zacatky slov nabidky.
 * „mleko 1 5" najde „Mleko 1,5%", ale ne „Mleko UHT 3,2%".
 */
export function offerMatchesTerms(terms, searchText) {
    if (terms.length === 0 || !searchText)
        return false;
    const words = searchText.split(' ');
    // Kmen a souznacna slova: „Plátkový sýr" najde „Plátky sýra" i „w plastrach" (searchAlternatives).
    return terms.some((tokens) => tokens.every((token) => searchAlternatives(token).some((alt) => words.some((word) => word.startsWith(alt)))));
}
/**
 * Akce pro jednu polozku nakupu. storeIds = obchody na trase, kde akce plati
 * (poradi trasy); prazdne = bezny nakup „kdekoli", polozka se nepresouva.
 */
export const TripDealCandidateSchema = z.object({
    storeIds: z.array(z.uuid()),
    offer: OfferSchema,
});
export const TripDealSchema = z.object({
    tripItemId: z.uuid(),
    /** catalog = hledane vyrazy zbozi skupiny; name = polozka psana rukou, shoda jen podle nazvu. */
    by: z.enum(['catalog', 'name']),
    candidates: z.array(TripDealCandidateSchema),
});
/** Slevy pro nekoupene polozky nakupu v den nakupu (bez data dnes). */
export const TripDealsResponseSchema = z.object({
    /** Den, pro ktery se hledalo, „2026-09-19". */
    date: z.string(),
    /** Do kdy jsou zname tydenni letaky obchodu na trase. */
    knownUntil: z.iso.datetime().nullable(),
    /** Obchody na trase bez letaku — jejich akce hledani nezna. */
    storesWithoutOffers: z.array(z.uuid()),
    items: z.array(TripDealSchema),
});
//# sourceMappingURL=promotions.js.map